"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

const Sheet = DialogPrimitive.Root;

const SheetTrigger = DialogPrimitive.Trigger;

const SheetPortal = DialogPrimitive.Portal;

const SheetOverlay = React.forwardRef<
	React.ComponentRef<typeof DialogPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Overlay
		className={cn(
			"bg-foreground/20 backdrop-blur-xs fixed inset-0 z-50",
			"data-[state=open]:animate-in data-[state=closed]:animate-out",
			"data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
			"[animation-timing-function:var(--ease-smooth-out)] data-[state=closed]:[animation-duration:var(--duration-medium)] data-[state=open]:[animation-duration:var(--duration-slow)] motion-reduce:!animate-none motion-reduce:!transition-none",
			className,
		)}
		{...props}
		ref={ref}
	/>
));
SheetOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface SheetContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
	side?: "top" | "bottom" | "left" | "right";
}

const sheetVariants = {
	top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
	bottom:
		"inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
	left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
	right:
		"inset-y-0 right-0 h-full w-full border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-md",
};

const SheetContent = React.forwardRef<React.ComponentRef<typeof DialogPrimitive.Content>, SheetContentProps>(
	({ side = "right", className, children, ...props }, ref) => (
		<SheetPortal>
			<SheetOverlay />
			<DialogPrimitive.Content
				ref={ref}
				className={cn(
					"fixed z-50 gap-4 bg-background shadow-lg",
					"data-[state=open]:animate-in data-[state=closed]:animate-out",
					"[animation-timing-function:var(--ease-smooth-out)] data-[state=closed]:[animation-duration:var(--duration-medium)] data-[state=open]:[animation-duration:var(--duration-slow)] motion-reduce:!animate-none motion-reduce:!transition-none",
					sheetVariants[side],
					className,
				)}
				{...props}
			>
				{children}
			</DialogPrimitive.Content>
		</SheetPortal>
	),
);
SheetContent.displayName = DialogPrimitive.Content.displayName;

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div className={cn("flex items-center gap-4", className)} {...props} />
);
SheetHeader.displayName = "SheetHeader";

const SheetTitle = React.forwardRef<
	React.ComponentRef<typeof DialogPrimitive.Title>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Title
		ref={ref}
		className={cn("text-lg font-semibold text-foreground", className)}
		{...props}
	/>
));
SheetTitle.displayName = DialogPrimitive.Title.displayName;

const SheetCloseButton = React.forwardRef<
	React.ComponentRef<typeof DialogPrimitive.Close>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close> & { closeLabel?: string }
>(({ className, closeLabel = "Close", ...props }, ref) => (
	<DialogPrimitive.Close
		ref={ref}
		className={cn(
			"outline-hidden rounded-md border-none bg-transparent p-2 opacity-70 shadow-none transition-opacity",
			"hover:bg-accent hover:opacity-100",
			"focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
			"disabled:pointer-events-none",
			className,
		)}
		{...props}
	>
		<X className="h-5 w-5" />
		<span className="sr-only">{closeLabel}</span>
	</DialogPrimitive.Close>
));
SheetCloseButton.displayName = "SheetCloseButton";

export { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle, SheetCloseButton };
